import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Logo from '../components/Logo';
import { Spinner } from '../components/Spinner';
import { supabase } from '../lib/supabase';

type FieldErrors = {
  email?: string;
  password?: string;
};

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function sanitizeLocationStateMessage(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const cleaned = raw.replace(/<[^>]*>/g, '').replace(/[<>]/g, '').trim().slice(0, 400);
  return cleaned.length > 0 ? cleaned : null;
}

async function checkEmailRegistered(email: string): Promise<boolean | null> {
  const { data, error } = await supabase.rpc('check_email_registered', {
    p_email: email.trim(),
  });
  if (error) return null;
  return Boolean(data);
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const messageParam = searchParams.get('message');
  const deletionScheduledMessage = messageParam === 'deletion_scheduled';
  const emailFromUrl = searchParams.get('email');
  const emailConfirmed = searchParams.get('confirmed') === 'true';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const passwordRef = useRef<HTMLInputElement>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [stateInfoBanner, setStateInfoBanner] = useState<string | null>(null);

  useEffect(() => {
    const msg = sanitizeLocationStateMessage((location.state as { message?: unknown } | null)?.message);
    if (!msg) return;
    setStateInfoBanner(msg);
    navigate('.', { replace: true, state: {} });
  }, [location.state, navigate]);

  useEffect(() => {
    const raw = emailFromUrl;
    if (!raw || !raw.trim()) return;
    try {
      setEmail(decodeURIComponent(raw.trim()));
    } catch {
      setEmail(raw.trim());
    }
  }, [emailFromUrl]);

  useEffect(() => {
    if (!emailFromUrl?.trim()) return;
    const t = window.setTimeout(() => {
      passwordRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [emailFromUrl]);

  function validate(): boolean {
    const next: FieldErrors = {};
    const em = email.trim();
    if (!em) next.email = 'Enter your email address.';
    else if (!isValidEmail(em)) next.email = 'Enter a valid email address.';
    if (!password) next.password = 'Enter your password.';
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!validate()) return;

    setSubmitting(true);
    const { data: signInData, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    if (error) {
      if (error.message === 'User is banned') {
        setFormError(
          'Your account access has been restricted. Please contact hello@lognsec.com for assistance.'
        );
        setSubmitting(false);
        return;
      }

      const msg = (error.message || '').toLowerCase();
      const invalidCreds =
        msg.includes('invalid login credentials') ||
        msg.includes('invalid_credentials') ||
        error.message === 'Invalid login credentials';

      if (invalidCreds) {
        const registered = await checkEmailRegistered(email);
        if (registered === true) {
          setFormError(
            "That password doesn't match. Try again or reset your password."
          );
        } else if (registered === false) {
          setFormError(
            "We couldn't find an account with that email. Want to sign up instead?"
          );
        } else {
          setFormError(
            "We couldn't sign you in. Check your email and password, or reset your password."
          );
        }
      } else {
        setFormError(error.message);
      }
      setSubmitting(false);
      return;
    }

    if (!signInData.session?.user) {
      setFormError('We could not verify your session. Please try again.');
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    navigate('/dashboard', { replace: true });
  }

  const inputClass =
    'w-full rounded-lg border border-border bg-navy px-3 py-2.5 text-sm text-foreground outline-none transition placeholder:text-muted focus:border-accent focus:ring-1 focus:ring-accent/40';

  return (
    <main className="min-h-screen bg-navy px-4 py-10 text-foreground sm:px-6 sm:py-14">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-6 flex justify-center">
            <Logo size="lg" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Welcome Back</h1>
          <p className="mt-2 text-sm text-muted sm:text-base">
            Your security is being watched. Log in to see your status.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-border bg-surface/80 p-6 shadow-xl backdrop-blur sm:p-8"
          noValidate
        >
          {emailConfirmed ? (
            <p
              className="mb-4 rounded-lg border border-accent/40 bg-accent/10 px-3 py-2 text-sm text-accent"
              role="status"
            >
              ✓ Email confirmed successfully. Please log in to continue.
            </p>
          ) : null}
          {stateInfoBanner ? (
            <p
              className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-950 dark:text-amber-100"
              role="status"
            >
              {stateInfoBanner}
            </p>
          ) : null}
          {deletionScheduledMessage ? (
            <p
              className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-950 dark:text-amber-100"
              role="status"
            >
              Your account has been scheduled for deletion. You will receive a confirmation email
              shortly. Log back in within 30 days to cancel.
            </p>
          ) : null}
          {!deletionScheduledMessage && messageParam ? (
            <p
              className="mb-4 rounded-lg border border-accent/40 bg-accent/10 px-3 py-2 text-sm text-accent"
              role="status"
            >
              {messageParam}
            </p>
          ) : null}
          {formError ? (
            <p
              className="mb-4 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
              role="alert"
            >
              {formError}
            </p>
          ) : null}

          <div className="space-y-4">
            <div>
              <label htmlFor="login-email" className="mb-1.5 block text-sm font-medium text-foreground">
                Email
              </label>
              <input
                id="login-email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`${inputClass} ${fieldErrors.email ? 'border-danger' : ''}`}
              />
              {fieldErrors.email ? (
                <p className="mt-1.5 text-sm text-danger">{fieldErrors.email}</p>
              ) : null}
            </div>

            <div>
              <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                <label htmlFor="login-password" className="text-sm font-medium text-foreground">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-accent hover:underline"
                >
                  Forgot your password?
                </Link>
              </div>
              <input
                ref={passwordRef}
                id="login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`${inputClass} ${fieldErrors.password ? 'border-danger' : ''}`}
              />
              {fieldErrors.password ? (
                <p className="mt-1.5 text-sm text-danger">{fieldErrors.password}</p>
              ) : null}
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="mt-6 flex w-full min-h-[48px] items-center justify-center gap-2 rounded-lg bg-accent px-4 py-3 text-sm font-semibold text-cta-text transition hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {submitting ? (
              <>
                <Spinner className="h-5 w-5 text-cta-text" />
                Signing in…
              </>
            ) : (
              'Log In to My Dashboard'
            )}
          </button>

          <p className="mt-6 text-center text-sm text-muted">
            Don&apos;t have an account?{' '}
            <Link to="/signup" className="font-medium text-accent hover:underline">
              Start your free trial
            </Link>
          </p>
        </form>
      </div>
    </main>
  );
}
